<template>
  <v-dialog v-model="dialog" max-width="1400px" class="fontNotoSans400" persistent>
    <div class="wageBeforeAfterModalCard">
      <div class="modal-header">
        <h2>비과세 적용 전후 비교표</h2>
        <v-icon class="close-btn" color="#333" @click="closeDialog">mdi-close</v-icon>
      </div>

      <div class="info-section">
        <div class="info-row">
          <div class="info-item">
            <span class="info-label">조회 연월</span>
            <span class="info-value highlight">{{ currentYearMonth }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">서비스 시작연월</span>
            <template v-if="!isEditingServiceDate">
              <span class="info-value">{{ serviceStartDate || '미설정' }}</span>
              <v-btn variant="text" size="x-small" color="grey" @click="isEditingServiceDate = true" class="edit-btn">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-else>
              <input type="month" v-model="editServiceDate" class="date-input" />
              <v-btn variant="text" size="x-small" color="primary" @click="saveServiceDate" :loading="isSavingDate">
                <v-icon size="small">mdi-check</v-icon>
              </v-btn>
              <v-btn variant="text" size="x-small" color="grey" @click="cancelEditServiceDate">
                <v-icon size="small">mdi-close</v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </div>

      <div class="action-section">
        <v-btn class="btnColorDark" @click="generatePdf" :disabled="comparisonData.length === 0">PDF 생성</v-btn>
        <template v-if="hasServiceStartYm && retiredSet.size > 0">
          <v-btn 
            :class="hideRetired ? 'btnColorOrange' : 'btnColorGrey'" 
            size="small"
            @click="hideRetired = !hideRetired"
          >
            <v-icon size="small" class="mr-1">{{ hideRetired ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
            제외된 인원 {{ hideRetired ? '보기' : '숨기기' }} ({{ retiredSet.size }}명)
          </v-btn>
        </template>
      </div>

      <WageBeforeAfterHelp />

      <div class="modal-content" v-if="comparisonData.length > 0">
        <v-tabs v-model="activeTab" color="primary">
          <v-tab value="detail">상세 비교</v-tab>
          <v-tab value="summary">요약</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <v-window-item value="detail">
            <div class="unit-label">단위: 원</div>
            <div class="comparison-table-wrapper">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th rowspan="2" class="sticky-col col-no">No</th>
                    <th rowspan="2" class="sticky-col-name">사원명</th>
                    <th rowspan="2">직종</th>
                    <th rowspan="2" v-if="hasServiceStartYm" class="retired-header">제외</th>
                    <th rowspan="2">총급여</th>
                    <th colspan="4" class="before-header">개선전</th>
                    <th colspan="4" class="after-header">개선후</th>
                    <th colspan="4" class="diff-header">차액</th>
                    <th rowspan="2" class="saving-header">절감액</th>
                  </tr>
                  <tr>
                    <th class="before-header">비과세</th>
                    <th class="before-header">기관부담</th>
                    <th class="before-header">퇴직금</th>
                    <th class="before-header">지급급여</th>
                    <th class="after-header">비과세</th>
                    <th class="after-header">기관부담</th>
                    <th class="after-header">퇴직금</th>
                    <th class="after-header">지급급여</th>
                    <th class="diff-sub-header">비과세</th>
                    <th class="diff-sub-header">기관부담</th>
                    <th class="diff-sub-header">퇴직금</th>
                    <th class="diff-sub-header">지급급여</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in displayComparisonData" :key="item.empName" :class="{ 'retired-row': retiredSet.has(item.empName) }">
                    <td class="sticky-col col-no text-center">{{ index + 1 }}</td>
                    <td class="sticky-col-name">{{ item.empName }}</td>
                    <td class="text-center">{{ item.empJob }}</td>
                    <td v-if="hasServiceStartYm" class="text-center retired-cell">
                      <input type="checkbox" :checked="retiredSet.has(item.empName)" @change="toggleRetired(item.empName)" title="서비스 시작 이전 퇴사자 체크" />
                    </td>
                    <td class="text-right tooltip-cell" :title="item.tooltip.total">{{ item.total }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeNonTax">{{ item.before.nonTax }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeCompanyIns">{{ item.before.companyIns }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeRetirement">{{ item.before.retirement }}</td>
                    <td class="before-cell text-right tooltip-cell" :title="item.tooltip.beforeNetPay">{{ item.before.netPay }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterNonTax">{{ item.after.nonTax }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterCompanyIns">{{ item.after.companyIns }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterRetirement">{{ item.after.retirement }}</td>
                    <td class="after-cell text-right tooltip-cell" :title="item.tooltip.afterNetPay">{{ item.after.netPay }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.nonTax)" :title="item.tooltip.diffNonTax">{{ item.diff.nonTax }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.companyIns, true)" :title="item.tooltip.diffCompanyIns">{{ item.diff.companyIns }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.retirement, true)" :title="item.tooltip.diffRetirement">{{ item.diff.retirement }}</td>
                    <td class="diff-cell text-right tooltip-cell" :class="getDiffClass(item.diff.netPay)" :title="item.tooltip.diffNetPay">{{ item.diff.netPay }}</td>
                    <td class="saving-cell text-right font-bold tooltip-cell" :class="getDiffClass(item.saving, true)" :title="item.tooltip.saving">{{ item.saving }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td class="sticky-col col-no font-bold" :colspan="hasServiceStartYm ? 4 : 3">합계</td>
                    <td class="text-right font-bold">{{ totals.total }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.nonTax }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.companyIns }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.retirement }}</td>
                    <td class="before-cell text-right font-bold">{{ totals.before.netPay }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.nonTax }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.companyIns }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.retirement }}</td>
                    <td class="after-cell text-right font-bold">{{ totals.after.netPay }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.nonTax)">{{ totals.diff.nonTax }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.companyIns, true)">{{ totals.diff.companyIns }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.retirement, true)">{{ totals.diff.retirement }}</td>
                    <td class="diff-cell text-right font-bold" :class="getDiffClass(totals.diff.netPay)">{{ totals.diff.netPay }}</td>
                    <td class="saving-cell text-right font-bold" :class="getDiffClass(totals.saving, true)">{{ totals.saving }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </v-window-item>

          <v-window-item value="summary">
            <div class="summary-section">
              <div class="summary-cards">
                <div class="summary-card before-card">
                  <h3>개선전 (생산직비과세 미적용)</h3>
                  <div class="summary-row">
                    <span class="label">총급여</span>
                    <span class="value">{{ totals.total }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">비과세</span>
                    <span class="value">{{ totals.before.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험</span>
                    <span class="value">{{ totals.before.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금</span>
                    <span class="value">{{ totals.before.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">지급급여 (실수령액)</span>
                    <span class="value">{{ totals.before.netPay }}</span>
                  </div>
                </div>

                <div class="summary-card after-card">
                  <h3>개선후 (생산직비과세 적용)</h3>
                  <div class="summary-row">
                    <span class="label">총급여</span>
                    <span class="value">{{ totals.total }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">비과세</span>
                    <span class="value">{{ totals.after.nonTax }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험</span>
                    <span class="value">{{ totals.after.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금</span>
                    <span class="value">{{ totals.after.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">지급급여 (실수령액)</span>
                    <span class="value">{{ totals.after.netPay }}</span>
                  </div>
                </div>

                <div class="summary-card diff-card">
                  <h3>기관 비용 절감 효과</h3>
                  <div class="summary-row highlight-row">
                    <span class="label">대상 인원</span>
                    <span class="value">{{ activeEmployeeCount }}명</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row">
                    <span class="label">기관부담 4대보험 차액</span>
                    <span class="value" :class="getDiffClass(totals.diff.companyIns, true)">{{ totals.diff.companyIns }}</span>
                  </div>
                  <div class="summary-row">
                    <span class="label">퇴직금 차액</span>
                    <span class="value" :class="getDiffClass(totals.diff.retirement, true)">{{ totals.diff.retirement }}</span>
                  </div>
                  <div class="divider"></div>
                  <div class="summary-row total">
                    <span class="label">월 절감액</span>
                    <span class="value large" :class="getDiffClass(totals.saving, true)">{{ totals.saving }}</span>
                  </div>
                  <div class="summary-row info">
                    <span class="label">연간 절감 예상액</span>
                    <span class="value" :class="getDiffClass(totals.saving, true)">{{ totals.yearSaving }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </div>

      <div class="modal-content empty-state" v-else>
        <p>데이터가 없습니다.</p>
      </div>

      <div class="modal-footer">
        <v-btn class="cancelRegistBtn" @click="closeDialog">닫기</v-btn>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import WageBeforeAfterHelp from "./WageBeforeAfterHelp.vue";

interface ComparisonItem {
  empName: string;
  empJob: string;
  total: string;
  before: { nonTax: string; companyIns: string; retirement: string; netPay: string };
  after: { nonTax: string; companyIns: string; retirement: string; netPay: string };
  diff: { nonTax: string; companyIns: string; retirement: string; netPay: string };
  saving: string;
  tooltip: {
    total: string;
    beforeNonTax: string; beforeCompanyIns: string; beforeRetirement: string; beforeNetPay: string;
    afterNonTax: string; afterCompanyIns: string; afterRetirement: string; afterNetPay: string;
    diffNonTax: string; diffCompanyIns: string; diffRetirement: string; diffNetPay: string;
    saving: string;
  };
}

const props = defineProps<{
  excelData: any[];
  calcData: any[];
}>();

const store = useStore();
const dialog = ref(false);
const activeTab = ref("detail");
const comparisonData = ref<ComparisonItem[]>([]);

// 서비스 시작연월 관련
const serviceStartDate = ref<string>("");
const rawServiceStartYm = ref<string>(""); // YYYY-MM 형식
const isEditingServiceDate = ref(false);
const editServiceDate = ref("");
const isSavingDate = ref(false);
const currentYearMonth = ref("");
const rawCurrentYm = ref<string>(""); // YYYY-MM 형식

// 퇴사자 선택 관련 (서비스 시작 이전 퇴사자 선택용)
const retiredSet = ref<Set<string>>(new Set());
const hasServiceStartYm = ref(false); // 서비스 시작연월이 설정되어 있는지
const allComparisonData = ref<ComparisonItem[]>([]); // 필터링 전 전체 데이터
const hideRetired = ref(false); // 제외된 인원 숨기기 토글

// 퇴사자 제외한 실제 인원수
const activeEmployeeCount = computed(() => {
  return allComparisonData.value.filter(item => !retiredSet.value.has(item.empName)).length;
});

// 화면에 표시할 데이터 (퇴사자 숨기기 적용)
const displayComparisonData = computed(() => {
  if (hideRetired.value) {
    return comparisonData.value.filter(item => !retiredSet.value.has(item.empName));
  }
  return comparisonData.value;
});

const totals = reactive({
  total: "0",
  before: { nonTax: "0", companyIns: "0", retirement: "0", netPay: "0" },
  after: { nonTax: "0", companyIns: "0", retirement: "0", netPay: "0" },
  diff: { nonTax: "0", companyIns: "0", retirement: "0", netPay: "0" },
  saving: "0",
  yearSaving: "0"
});

const nc = (value: any): number => {
  if (value == null) return 0;
  return parseFloat(String(value).replace(/,/g, "")) || 0;
};

const ac = (number: number): string => {
  if (number === 0) return "0";
  return Math.round(number).toLocaleString();
};

const formatYearMonth = (ym: string): string => {
  if (!ym) return "";
  const parts = ym.split("-");
  if (parts.length === 2) {
    return `${parts[0]}년 ${parseInt(parts[1], 10)}월`;
  }
  return ym;
};

const fetchServiceStartDate = async () => {
  try {
    const sisulId = store.state.AdminSisulId;
    const response = await axios.get(`/api/readSsId/${sisulId}`);
    if (response.data?.result) {
      const wageIn = response.data.result.sisul_wage_in;
      serviceStartDate.value = wageIn ? formatYearMonth(wageIn) : "";
      rawServiceStartYm.value = wageIn || "";
      editServiceDate.value = wageIn || "";
    }
  } catch (error) {
    console.error("서비스 시작연월 조회 실패:", error);
  }
};

const saveServiceDate = async () => {
  if (!editServiceDate.value) return;
  isSavingDate.value = true;
  try {
    const sisulId = store.state.AdminSisulId;
    await axios.post("/api/updateSsWageIn", {
      sisul_id: sisulId,
      sisul_wage_in: editServiceDate.value
    });
    serviceStartDate.value = formatYearMonth(editServiceDate.value);
    rawServiceStartYm.value = editServiceDate.value;
    isEditingServiceDate.value = false;
    store.commit("showSnackbar", { text: "서비스 시작연월이 저장되었습니다.", color: "success" });
    updateHasServiceStartYm();
    retiredSet.value = new Set(); // 서비스시작월 변경시 제외 인원 초기화
    calculateComparison();
  } catch (error) {
    console.error("서비스 시작연월 저장 실패:", error);
    store.commit("showSnackbar", { text: "저장 중 오류가 발생했습니다.", color: "error" });
  } finally {
    isSavingDate.value = false;
  }
};

const cancelEditServiceDate = () => {
  isEditingServiceDate.value = false;
};

const toggleRetired = (empName: string) => {
  if (retiredSet.value.has(empName)) {
    retiredSet.value.delete(empName);
  } else {
    retiredSet.value.add(empName);
  }
  retiredSet.value = new Set(retiredSet.value);
  recalculateTotals();
};

const updateHasServiceStartYm = () => {
  hasServiceStartYm.value = !!rawServiceStartYm.value;
};

const openDialog = async () => {
  dialog.value = true;
  rawCurrentYm.value = store.state.AdminYYMM;
  currentYearMonth.value = formatYearMonth(store.state.AdminYYMM);
  retiredSet.value = new Set();
  hideRetired.value = false;
  await fetchServiceStartDate();
  updateHasServiceStartYm();
  calculateComparison();
};

const closeDialog = () => {
  dialog.value = false;
};

const getDiffClass = (value: string, isReverse = false) => {
  const numValue = nc(value);
  if (numValue === 0) return "";
  if (isReverse) {
    return numValue < 0 ? "positive-diff" : "negative-diff";
  }
  return numValue > 0 ? "positive-diff" : "negative-diff";
};

const recalculateTotals = () => {
  const sum = { total: 0 };
  const sumBefore = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };
  const sumAfter = { nonTax: 0, companyIns: 0, retirement: 0, netPay: 0 };

  // 퇴사자 체크된 사원 제외하고 합계 계산
  const activeData = allComparisonData.value.filter(item => !retiredSet.value.has(item.empName));

  activeData.forEach(item => {
    sum.total += nc(item.total);
    sumBefore.nonTax += nc(item.before.nonTax);
    sumBefore.companyIns += nc(item.before.companyIns);
    sumBefore.retirement += nc(item.before.retirement);
    sumBefore.netPay += nc(item.before.netPay);
    sumAfter.nonTax += nc(item.after.nonTax);
    sumAfter.companyIns += nc(item.after.companyIns);
    sumAfter.retirement += nc(item.after.retirement);
    sumAfter.netPay += nc(item.after.netPay);
  });

  totals.total = ac(sum.total);
  totals.before.nonTax = ac(sumBefore.nonTax);
  totals.before.companyIns = ac(sumBefore.companyIns);
  totals.before.retirement = ac(sumBefore.retirement);
  totals.before.netPay = ac(sumBefore.netPay);
  totals.after.nonTax = ac(sumAfter.nonTax);
  totals.after.companyIns = ac(sumAfter.companyIns);
  totals.after.retirement = ac(sumAfter.retirement);
  totals.after.netPay = ac(sumAfter.netPay);
  totals.diff.nonTax = ac(sumAfter.nonTax - sumBefore.nonTax);
  totals.diff.companyIns = ac(sumAfter.companyIns - sumBefore.companyIns);
  totals.diff.retirement = ac(sumAfter.retirement - sumBefore.retirement);
  totals.diff.netPay = ac(sumAfter.netPay - sumBefore.netPay);
  totals.saving = ac((sumAfter.companyIns - sumBefore.companyIns) + (sumAfter.retirement - sumBefore.retirement));
  totals.yearSaving = ac(((sumAfter.companyIns - sumBefore.companyIns) + (sumAfter.retirement - sumBefore.retirement)) * 12);

  // 화면에 표시되는 comparisonData는 전체 데이터 (필터링 없이)
  comparisonData.value = allComparisonData.value;
};

const calculateComparison = () => {
  if (!props.calcData || props.calcData.length === 0) return;

  allComparisonData.value = props.calcData.map((calc: any) => {
    const excel = props.excelData.find((e) => e.empName === calc.empName);

    const total = nc(calc.total);

    // 개선후 (calcData - 생산직비과세 적용)
    const afterTaxable = nc(calc.taxable);
    const afterNonTax = total - afterTaxable;
    const afterDeduction = nc(calc.l_total);
    const afterNetPay = total - afterDeduction;
    const afterRetirement = nc(calc.c_out);

    const after_c_yeon = nc(calc.c_yeon);
    const after_c_gun = nc(calc.c_gun);
    const after_c_jang = nc(calc.c_jang);
    const after_c_go = nc(calc.c_go);
    const after_c_sil = nc(calc.c_sil);
    const after_c_san = nc(calc.c_san);
    const afterCompanyIns = after_c_yeon + after_c_gun + after_c_jang + after_c_go + after_c_sil + after_c_san;

    // 개선후 비과세 항목
    const afterDang = nc(calc.dang);
    const afterLunch = nc(calc.lunch);
    const afterCar = nc(calc.car);
    const afterChildcare = nc(calc.childcare);
    const afterProductionNonTax = afterNonTax - afterDang - afterLunch - afterCar - afterChildcare;

    // 개선전 (excelData 기반 - 생산직비과세 미적용)
    let beforeNonTax = 0;
    let beforeTaxable = 0;
    let beforeCompanyIns = 0;
    let beforeRetirement = 0;
    let beforeNetPay = 0;
    let beforeDeduction = afterDeduction;

    let beforeDang = 0, beforeLunch = 0, beforeCar = 0, beforeChildcare = 0;
    let before_c_yeon = 0, before_c_gun = 0, before_c_jang = 0, before_c_go = 0, before_c_sil = 0, before_c_san = 0;

    if (excel) {
      beforeDang = nc(excel.dang);
      beforeLunch = nc(excel.lunch);
      beforeCar = nc(excel.car);
      beforeChildcare = nc(excel.childcare);

      // 개선전 비과세 (생산직비과세 미적용)
      beforeNonTax = Math.floor((beforeDang + beforeLunch + beforeCar + beforeChildcare) / 10) * 10;
      beforeTaxable = Math.floor((total - beforeNonTax) / 10) * 10;

      beforeNetPay = total - beforeDeduction;

      // 개선전 기관부담 (과세금액 기준으로 계산)
      before_c_yeon = after_c_yeon; // 국민연금은 동일
      before_c_san = after_c_san; // 산재보험은 동일

      // 과세금액 비율로 기관부담 계산
      if (afterTaxable > 0) {
        before_c_gun = after_c_gun === 0 ? 0 : Math.floor((beforeTaxable * (after_c_gun / afterTaxable)) / 10) * 10;
        before_c_jang = (after_c_jang === 0 || after_c_gun === 0) ? 0 : Math.floor((before_c_gun * (after_c_jang / after_c_gun)) / 10) * 10;
        before_c_go = after_c_go === 0 ? 0 : Math.floor((beforeTaxable * (after_c_go / afterTaxable)) / 10) * 10;
        before_c_sil = after_c_sil === 0 ? 0 : Math.floor((beforeTaxable * (after_c_sil / afterTaxable)) / 10) * 10;
      }

      beforeCompanyIns = before_c_yeon + before_c_gun + before_c_jang + before_c_go + before_c_sil + before_c_san;

      // 개선전 퇴직금
      beforeRetirement = afterRetirement === 0 ? 0 : Math.floor(((total - beforeLunch) / 12) / 10) * 10;
    }

    // 차액 계산
    const diffNonTax = afterNonTax - beforeNonTax;
    const diffCompanyIns = afterCompanyIns - beforeCompanyIns;
    const diffRetirement = afterRetirement - beforeRetirement;
    const diffNetPay = afterNetPay - beforeNetPay;
    const saving = diffCompanyIns + diffRetirement;

    // 툴팁 생성
    const tooltipTotal = `[총급여]\n= ${ac(total)}`;

    const tooltipBeforeNonTax = `[개선전 비과세]\n\n` +
      `※ 생산직비과세 미적용\n\n` +
      `◆ 비과세 항목\n` +
      `  당직수당: ${ac(beforeDang)}\n` +
      `  중식대: ${ac(beforeLunch)}\n` +
      `  차량비: ${ac(beforeCar)}\n` +
      `  양육비: ${ac(beforeChildcare)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 비과세 합계: ${ac(beforeNonTax)}`;

    const tooltipBeforeCompanyIns = `[개선전 기관부담 4대보험]\n\n` +
      `◆ 엑셀업로드 데이터 기준\n\n` +
      `  국민연금: ${ac(before_c_yeon)}\n` +
      `  건강보험: ${ac(before_c_gun)}\n` +
      `  장기요양: ${ac(before_c_jang)}\n` +
      `  고용보험: ${ac(before_c_go)}\n` +
      `  실업급여: ${ac(before_c_sil)}\n` +
      `  산재보험: ${ac(before_c_san)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 기관부담 합계: ${ac(beforeCompanyIns)}`;

    const tooltipBeforeRetirement = `[개선전 퇴직금]\n\n` +
      `◆ 계산식\n  (총급여 - 중식대) ÷ 12\n\n` +
      `◆ 산출내역\n` +
      `  총급여: ${ac(total)}\n` +
      `  (-) 중식대: ${ac(beforeLunch)}\n` +
      `  = ${ac(total - beforeLunch)} ÷ 12\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 퇴직금: ${ac(beforeRetirement)}`;

    const tooltipBeforeNetPay = `[개선전 지급급여]\n\n` +
      `◆ 총급여 - 근로자공제\n` +
      `  ${ac(total)} - ${ac(beforeDeduction)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 지급급여: ${ac(beforeNetPay)}`;

    const tooltipAfterNonTax = `[개선후 비과세]\n\n` +
      `※ 생산직비과세 적용\n\n` +
      `◆ 비과세 항목\n` +
      `  생산직비과세: ${ac(afterProductionNonTax)}\n` +
      `  당직수당: ${ac(afterDang)}\n` +
      `  중식대: ${ac(afterLunch)}\n` +
      `  차량비: ${ac(afterCar)}\n` +
      `  양육비: ${ac(afterChildcare)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 비과세 합계: ${ac(afterNonTax)}`;

    const tooltipAfterCompanyIns = `[개선후 기관부담 4대보험]\n\n` +
      `◆ 과세금액 ${ac(afterTaxable)} 기준\n\n` +
      `  국민연금: ${ac(after_c_yeon)}\n` +
      `  건강보험: ${ac(after_c_gun)}\n` +
      `  장기요양: ${ac(after_c_jang)}\n` +
      `  고용보험: ${ac(after_c_go)}\n` +
      `  실업급여: ${ac(after_c_sil)}\n` +
      `  산재보험: ${ac(after_c_san)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 기관부담 합계: ${ac(afterCompanyIns)}`;

    const tooltipAfterRetirement = `[개선후 퇴직금]\n\n` +
      `◆ 계산된 퇴직금\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 퇴직금: ${ac(afterRetirement)}`;

    const tooltipAfterNetPay = `[개선후 지급급여]\n\n` +
      `◆ 총급여 - 근로자공제\n` +
      `  ${ac(total)} - ${ac(afterDeduction)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 지급급여: ${ac(afterNetPay)}`;

    const tooltipDiffNonTax = `[비과세 차액]\n\n` +
      `◆ 개선후 - 개선전\n` +
      `  ${ac(afterNonTax)} - ${ac(beforeNonTax)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffNonTax)}`;

    const tooltipDiffCompanyIns = `[기관부담 차액]\n\n` +
      `◆ 개선후 - 개선전\n\n` +
      `  국민연금: ${ac(after_c_yeon - before_c_yeon)}\n` +
      `  건강보험: ${ac(after_c_gun - before_c_gun)}\n` +
      `  장기요양: ${ac(after_c_jang - before_c_jang)}\n` +
      `  고용보험: ${ac(after_c_go - before_c_go)}\n` +
      `  실업급여: ${ac(after_c_sil - before_c_sil)}\n` +
      `  산재보험: ${ac(after_c_san - before_c_san)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffCompanyIns)}`;

    const tooltipDiffRetirement = `[퇴직금 차액]\n\n` +
      `◆ 개선후 - 개선전\n` +
      `  ${ac(afterRetirement)} - ${ac(beforeRetirement)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffRetirement)}`;

    const tooltipDiffNetPay = `[지급급여 차액]\n\n` +
      `◆ 개선후 - 개선전\n` +
      `  ${ac(afterNetPay)} - ${ac(beforeNetPay)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 차액: ${ac(diffNetPay)}`;

    const tooltipSaving = `[기관 절감액]\n\n` +
      `◆ 기관부담 차액 + 퇴직금 차액\n` +
      `  ${ac(diffCompanyIns)} + ${ac(diffRetirement)}\n` +
      `━━━━━━━━━━━━━━━━━━━━\n` +
      `= 절감액: ${ac(saving)}`;

    return {
      empName: calc.empName,
      empJob: calc.empJob || "",
      total: ac(total),
      before: {
        nonTax: ac(beforeNonTax),
        companyIns: ac(beforeCompanyIns),
        retirement: ac(beforeRetirement),
        netPay: ac(beforeNetPay)
      },
      after: {
        nonTax: ac(afterNonTax),
        companyIns: ac(afterCompanyIns),
        retirement: ac(afterRetirement),
        netPay: ac(afterNetPay)
      },
      diff: {
        nonTax: ac(diffNonTax),
        companyIns: ac(diffCompanyIns),
        retirement: ac(diffRetirement),
        netPay: ac(diffNetPay)
      },
      saving: ac(saving),
      tooltip: {
        total: tooltipTotal,
        beforeNonTax: tooltipBeforeNonTax,
        beforeCompanyIns: tooltipBeforeCompanyIns,
        beforeRetirement: tooltipBeforeRetirement,
        beforeNetPay: tooltipBeforeNetPay,
        afterNonTax: tooltipAfterNonTax,
        afterCompanyIns: tooltipAfterCompanyIns,
        afterRetirement: tooltipAfterRetirement,
        afterNetPay: tooltipAfterNetPay,
        diffNonTax: tooltipDiffNonTax,
        diffCompanyIns: tooltipDiffCompanyIns,
        diffRetirement: tooltipDiffRetirement,
        diffNetPay: tooltipDiffNetPay,
        saving: tooltipSaving
      }
    };
  });

  recalculateTotals();
};

const generatePdf = async () => {
  try {
    const response = await axios.post(
      "/api/getWageBeforeAfterPdf",
      {
        comparisonData: comparisonData.value,
        totals: { total: totals.total, before: totals.before, after: totals.after, diff: totals.diff, saving: totals.saving, yearSaving: totals.yearSaving },
        sisulName: store.state.AdminSisulName,
        yearMonth: store.state.AdminYYMM
      },
      { responseType: "json" }
    );

    const pdfBase64 = response.data.pdf;
    const binaryString = window.atob(pdfBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.focus();
    }
  } catch (error) {
    console.error("PDF 생성 실패:", error);
    store.commit("showSnackbar", {
      text: "PDF 생성 중 오류가 발생했습니다.",
      color: "error"
    });
  }
};

defineExpose({ dialog, closeDialog, openDialog });
</script>

<style scoped>
.wageBeforeAfterModalCard {
  background-color: #fff;
  padding: 24px;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-header h2 {
  margin: 0;
  font-weight: 600;
}

.close-btn {
  width: 35px;
  height: 35px;
  cursor: pointer;
}

.info-section {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  font-size: 14px;
}

.info-value {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.info-value.highlight {
  color: #1976d2;
  font-weight: 700;
}

.edit-btn {
  min-width: auto !important;
  padding: 0 4px !important;
}

.date-input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 14px;
  font-family: inherit;
}

.action-section {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  align-items: center;
}

.btnColorOrange {
  background-color: #ff9800 !important;
  color: #fff !important;
}

.btnColorGrey {
  background-color: #9e9e9e !important;
  color: #fff !important;
}

.modal-content {
  min-height: 200px;
}

.unit-label {
  text-align: right;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  padding-right: 10px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.comparison-table-wrapper {
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  margin-top: 16px;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.comparison-table th,
.comparison-table td {
  border: 1px solid #ddd;
  padding: 6px 10px;
  white-space: nowrap;
}

.comparison-table thead th {
  background-color: #f5f5f5;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.comparison-table thead tr:first-child th {
  top: 0;
}

.comparison-table thead tr:nth-child(2) th {
  top: 32px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background-color: #fff;
  z-index: 5;
  font-weight: 500;
}

.col-no {
  width: 35px;
  min-width: 35px;
}

.sticky-col-name {
  position: sticky;
  left: 35px;
  background-color: #fff;
  z-index: 5;
  font-weight: 500;
}

.comparison-table thead .sticky-col,
.comparison-table thead .sticky-col-name {
  z-index: 15;
  background-color: #f5f5f5;
}

.total-row .sticky-col,
.total-row .sticky-col-name {
  background-color: #f0f0f0 !important;
}

.before-header {
  background-color: #fff3e0 !important;
  color: #e65100;
}

.after-header {
  background-color: #e8f5e9 !important;
  color: #2e7d32;
}

.diff-header {
  background-color: #e3f2fd !important;
  color: #1565c0;
}

.diff-sub-header {
  background-color: #e3f2fd !important;
  color: #1565c0;
  font-size: 11px;
}

.before-cell {
  background-color: #fffaf5;
}

.after-cell {
  background-color: #f5fff5;
}

.diff-cell {
  background-color: #f5f9ff;
}

.saving-header {
  background-color: #fce4ec !important;
  color: #c2185b;
}

.saving-cell {
  background-color: #fff5f7;
}

.retired-header {
  background-color: #ffebee !important;
  color: #c62828;
  font-size: 11px;
  width: 50px;
}

.retired-cell {
  background-color: #fff;
}

.retired-cell input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #c62828;
}

.retired-row {
  background-color: #ffebee !important;
  opacity: 0.7;
}

.retired-row td {
  text-decoration: line-through;
  color: #999;
}

.retired-row .sticky-col,
.retired-row .sticky-col-name {
  background-color: #ffebee !important;
}

.retired-row .retired-cell {
  text-decoration: none;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.font-bold {
  font-weight: 700;
}

.total-row td {
  background-color: #f0f0f0 !important;
  font-weight: 600;
}

.tooltip-cell {
  cursor: help;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
}

.positive-diff {
  color: #2e7d32;
}

.negative-diff {
  color: #d32f2f;
}

.summary-section {
  padding: 20px 0;
}

.summary-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 300px;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #ddd;
}

.summary-card h3 {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
}

.before-card {
  background-color: #fff8f0;
  border-color: #ffcc80;
}

.before-card h3 {
  color: #e65100;
}

.after-card {
  background-color: #f0fff0;
  border-color: #a5d6a7;
}

.after-card h3 {
  color: #2e7d32;
}

.diff-card {
  background-color: #f0f7ff;
  border-color: #90caf9;
}

.diff-card h3 {
  color: #1565c0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
}

.summary-row .label {
  color: #666;
}

.summary-row .value {
  font-weight: 500;
}

.summary-row.total {
  border-top: 1px solid #ddd;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 600;
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 600;
}

.summary-row.highlight-row {
  background-color: #e3f2fd;
  margin: -8px -14px 0 -14px;
  padding: 10px 14px;
  border-radius: 4px;
}

.summary-row.highlight-row .value {
  font-weight: 600;
  color: #1565c0;
}

.divider {
  height: 1px;
  background-color: #ddd;
  margin: 12px 0;
}

.large {
  font-size: 18px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  margin-top: 20px;
}
</style>
